import api, {
    AmortizationType, LoanPricingResultDetail, ManualLoanEntry, WebSocketEventType, amortizationTypeDisplay
} from '@api';
import {
    DialogContent, FormControl, FormLabel, Paper, Typography
} from '@mui/material';
import {
    Button, CurrencyField, DialogActions,
    PaperSaveLoader, PercentField, Radio, RadioGroup, RoutedDialog,
    RoutedDialogImplProps,
    Switch,
    TextField, isAWholeNumber, isAtLeast, isAtMost, useForm, usePageMessage
} from '@tsp-ui/core';
import { useGetCurrentAccount, useRenderTogglableEnumOptions } from '@utils';
import { ClientContext } from '@views/AuthenticatedRouteSwitch';
import { useContext, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { ManualLoanEntryFormValues } from '../ManualLoanEntryPage';

import { LoanPricingResultDetails } from './LoanPricingResultCard';
import styles from './PricingExplorerDialog.module.scss';


export default function PricingExplorerDialog(props: RoutedDialogImplProps) {
    const pageMessage = usePageMessage();
    const { id: clientId, customerId } = useGetCurrentAccount();
    const renderTogglableEnumOptions = useRenderTogglableEnumOptions();
    const navigate = useNavigate();
    const formMethods = useForm<ManualLoanEntry>();
    const { watch } = formMethods;
    const amorType = watch('amorType');
    const interestRate = watch('interestRate');
    const formValues = watch(); // Watch all form values

    const [ scenarioPricingResultDetail, setScenarioPricingResultDetail ] = useState<LoanPricingResultDetail>();
    const [ loading, setLoading ] = useState(false);

    const handleSubmit = formMethods.handleSubmit(async (formData) => {
        setLoading(true);

        try {
            const filteredFormData = Object.fromEntries(
                Object.entries(formData).filter(([ _, value ]) => value !== undefined && value !== '' && value !== null)
            ) as Partial<ManualLoanEntry>;

            setScenarioPricingResultDetail(
                await api.pricing.getScenarioPricingResultDetail(
                    clientId, filteredFormData as ManualLoanEntry, customerId
                )
            );

            pageMessage.success('Scenario pricing results fetched successfully');

            const unsubscribe:() => void = api.webSocket.subscribe(
                WebSocketEventType.PRICING_COMPLETE,
                () => {
                    navigate('..');
                    unsubscribe();
                }
            );
        } catch (error) {
            pageMessage.handleApiError('An error occurred while fetching scenario pricing results', error);
        }

        setLoading(false);
    });

    const { configurableValues } = useContext(ClientContext);
    const allowFixed = configurableValues?.togglableValues.amortizationType.includes(AmortizationType.FIXED);
    const allowAdjustable = configurableValues?.togglableValues.amortizationType.includes(AmortizationType.ADJUSTABLE);

    return (
        <RoutedDialog
            {...props}
            title="Pricing Explorer"
            fullScreen
            classes={{ paper: styles.dialogRoot }}
            className={styles.root}
        >
            <DialogContent
                className={styles.content}
            >
                <Paper className={styles.formWrapper}>
                    <form
                        noValidate
                        id="pricing-explorer-form"
                        className={styles.form}
                        onSubmit={handleSubmit}
                    >
                        <FormProvider {...formMethods}>
                            <div>
                                <FormLabel className={styles.formLabel}>
                                    Loan Details
                                </FormLabel>

                                <div className={styles.detailsContainer}>
                                    <CurrencyField<ManualLoanEntryFormValues>
                                        name="baseLoanAmount"
                                        label="Base loan amount"
                                        required
                                        rules={isAtLeast(1)}
                                    />

                                    <PercentField<ManualLoanEntryFormValues>
                                        name="interestRate"
                                        label="Interest rate"
                                        rules={isAtLeast(0)}
                                        noConversion
                                    />

                                    <TextField<ManualLoanEntryFormValues>
                                        name="lockPeriod"
                                        label="Lock period (days)"
                                        select
                                        defaultValue="30"
                                    >
                                        {renderTogglableEnumOptions('lockPeriod')}
                                    </TextField>

                                    <TextField<ManualLoanEntryFormValues>
                                        name="loanFICO"
                                        label="Loan FICO"
                                        type="number"
                                        rules={{
                                            ...isAtLeast(300),
                                            ...isAtMost(850)
                                        }}
                                    />

                                    <TextField<ManualLoanEntryFormValues>
                                        name="loanType"
                                        label="Loan type"
                                        select
                                        maxLength={100}
                                    >
                                        {renderTogglableEnumOptions('loanType')}
                                    </TextField>

                                    <TextField<ManualLoanEntryFormValues>
                                        name="loanTerm"
                                        label="Term (months)"
                                        type="number"
                                        required
                                        rules={{
                                            ...isAtLeast(1),
                                            ...isAWholeNumber
                                        }}
                                    />

                                    <TextField<ManualLoanEntryFormValues>
                                        name="loanLimitType"
                                        label="Loan limit type"
                                        select
                                        maxLength={100}
                                    >
                                        {renderTogglableEnumOptions('loanLimitType')}
                                    </TextField>

                                    <br />

                                    <Switch<ManualLoanEntryFormValues>
                                        name="escrowsFlag"
                                        label="Taxes & insurance escrowed"
                                    />

                                    <FormControl
                                        component="fieldset"
                                        className={styles.amortType}
                                        required
                                    >
                                        <FormLabel
                                            component="legend"
                                        >
                                            Amortization type
                                        </FormLabel>

                                        <RadioGroup<ManualLoanEntryFormValues>
                                            name="amorType"
                                            defaultValue={AmortizationType.FIXED}
                                        >
                                            {allowFixed && (
                                                <Radio
                                                    value={AmortizationType.FIXED}
                                                    label={amortizationTypeDisplay[AmortizationType.FIXED]}
                                                />
                                            )}

                                            {allowAdjustable && (
                                                <Radio
                                                    value={AmortizationType.ADJUSTABLE}
                                                    label={amortizationTypeDisplay[AmortizationType.ADJUSTABLE]}
                                                />
                                            )}

                                            {!allowFixed && !allowAdjustable && (
                                                <Typography>
                                                    No amortization types are enabled for this client.
                                                </Typography>
                                            )}
                                        </RadioGroup>
                                    </FormControl>

                                    {amorType === AmortizationType.ADJUSTABLE ? (
                                        <FormControl
                                            component="fieldset"
                                        >
                                            <FormLabel
                                                component="legend"
                                                required
                                            >
                                                Amortization details
                                            </FormLabel>

                                            <div className={styles.amortizationDetailsFields}>
                                                <CurrencyField<ManualLoanEntryFormValues>
                                                    name="armMargin"
                                                    label="Margin"
                                                    size="small"
                                                    required
                                                    hideRequiredIndicator
                                                    rules={isAtLeast(0)}
                                                />

                                                <CurrencyField<ManualLoanEntryFormValues>
                                                    name="armInitialCap"
                                                    label="Initial cap"
                                                    size="small"
                                                    required
                                                    hideRequiredIndicator
                                                    rules={isAtLeast(0)}
                                                />

                                                <CurrencyField<ManualLoanEntryFormValues>
                                                    name="armSubsequentCaps"
                                                    label="Subsequent cap"
                                                    size="small"
                                                    required
                                                    hideRequiredIndicator
                                                    rules={isAtLeast(0)}
                                                />

                                                <CurrencyField<ManualLoanEntryFormValues>
                                                    name="armLifeCap"
                                                    label="Life cap"
                                                    size="small"
                                                    required
                                                    hideRequiredIndicator
                                                    rules={isAtLeast(0)}
                                                />
                                            </div>
                                        </FormControl>
                                    ) : <div />}
                                </div>
                            </div>

                            <div>
                                <FormLabel className={styles.formLabel}>
                                    Property Details
                                </FormLabel>

                                <div className={styles.detailsContainer}>
                                    <TextField<ManualLoanEntryFormValues>
                                        name="propertyType"
                                        label="Property type"
                                        select
                                    >
                                        {renderTogglableEnumOptions('propertyType')}
                                    </TextField>

                                    <TextField<ManualLoanEntryFormValues>
                                        name="propertyUnits"
                                        label="Number of units"
                                        select
                                        defaultValue="1"
                                    >
                                        {renderTogglableEnumOptions('numUnits')}
                                    </TextField>

                                    <TextField<ManualLoanEntryFormValues>
                                        name="loanPurpose"
                                        label="Purpose"
                                        select
                                    >
                                        {renderTogglableEnumOptions('loanPurpose')}
                                    </TextField>

                                    <TextField<ManualLoanEntryFormValues>
                                        name="occupancyType"
                                        label="Occupancy"
                                        select
                                    >
                                        {renderTogglableEnumOptions('occupancyType')}
                                    </TextField>

                                    <CurrencyField<ManualLoanEntryFormValues>
                                        name="appraisedValue"
                                        label="Appraised value"
                                        rules={isAtLeast(0)}
                                    />

                                    <TextField<ManualLoanEntryFormValues>
                                        name="propertyState"
                                        label="State"
                                        required
                                        select
                                    >
                                        {renderTogglableEnumOptions('state')}
                                    </TextField>

                                </div>
                            </div>

                            <div>
                                <FormLabel className={styles.formLabel}>
                                    Additional Details
                                </FormLabel>

                                <div className={styles.detailsContainer}>
                                    <TextField<ManualLoanEntryFormValues>
                                        name="specialtyProgram"
                                        label="Specialty program"
                                        select
                                    >
                                        {renderTogglableEnumOptions('specialtyProgram')}
                                    </TextField>

                                    <CurrencyField<ManualLoanEntryFormValues>
                                        name="subordinatedBalance"
                                        label="Subordinated balance"
                                        rules={isAtLeast(0)}
                                    />

                                    <TextField<ManualLoanEntryFormValues>
                                        name="documentationType"
                                        label="Documentation"
                                        select
                                    >
                                        {renderTogglableEnumOptions('documentationType')}
                                    </TextField>

                                    <TextField<ManualLoanEntryFormValues>
                                        name="commitmentType"
                                        label="Commitment type"
                                        select
                                    >
                                        {renderTogglableEnumOptions('commitmentType')}
                                    </TextField>

                                    <TextField<ManualLoanEntryFormValues>
                                        name="productCode"
                                        label="Product code"
                                        maxLength={100}
                                    />

                                    <TextField<ManualLoanEntryFormValues>
                                        name="automatedUwSystem"
                                        label="UW System"
                                        select
                                    >
                                        {renderTogglableEnumOptions('automatedUnderwritingSystem')}
                                    </TextField>

                                    <Switch<ManualLoanEntryFormValues>
                                        name="firstTimeHomeBuyer"
                                        label="First time home buyer"
                                    />
                                </div>
                            </div>
                        </FormProvider>
                    </form>
                </Paper>

                <Paper className={styles.pricingResultsSection}>
                    <Typography
                        variant="h6"
                        className={styles.pricingResultsTitle}
                    >
                        Pricing Results
                    </Typography>


                    <div className={styles.pricingResults}>
                        {scenarioPricingResultDetail ? (
                            <div className={styles.resultPaper}>
                                <LoanPricingResultDetails
                                    interestRate={interestRate || 0}
                                    selectedNoteRate={null}
                                    scenarioPricingResultDetail={scenarioPricingResultDetail}
                                    formValues={formValues}
                                />
                            </div>
                        ) : (
                            <Typography>
                                Search to see scenario pricing results.
                            </Typography>
                        )}
                    </div>

                    <PaperSaveLoader loading={loading} />
                </Paper>
            </DialogContent>

            <DialogActions
                className={styles.actions}
            >
                <Button
                    variant="contained"
                    type="submit"
                    form="pricing-explorer-form"
                    disabled={loading}
                >
                    Search
                </Button>
            </DialogActions>
        </RoutedDialog>
    );
}
